import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Callout,
  ComparisonTable,
  MarketingPage,
  Section,
} from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";
import { MEASURED, MEASURED_SOURCE } from "@/lib/measured-stats";

const PATH = "/aeo-vs-seo";
const TITLE = "AEO vs SEO: What Actually Changes in the Work";
const DESCRIPTION =
  "SEO earns a ranked link. AEO earns a place inside the answer a buyer reads instead of the links. The practical differences: what you write, what you measure, and what you keep doing from SEO.";

const FAQS: FAQ[] = [
  {
    q: "What is the difference between AEO and SEO?",
    a: "SEO optimizes for position in a ranked list of links, and its outcome is a click. AEO (answer engine optimization) optimizes for inclusion in the generated answer an assistant writes above or instead of those links, and its outcome is being named and cited. Same site, different unit of success.",
  },
  {
    q: "Does AEO replace SEO?",
    a: "No. Answer engines retrieve from the indexed web, so crawlability, page speed, canonical clarity, and genuine topical depth still decide whether your page can be pulled in at all. AEO adds a layer on top of working SEO; it does not substitute for it.",
  },
  {
    q: "Can a page rank first and still be left out of AI answers?",
    a: "Yes, and it is common. Ranking rewards relevance and links; retrieval rewards pages that state a checkable answer in a form the model can lift. A long, unstructured page can hold position one and never be quoted.",
  },
  {
    q: "What do you measure for AEO instead of rankings?",
    a: "Mention rate (how often you are named), citation rate (how often you are linked as a source), and position within the answer, measured across repeated runs of the same buyer questions on each assistant. A single run is not a measurement.",
  },
  {
    q: "Which SEO habits hurt AEO?",
    a: "Keyword-shaped headings that never state an answer, burying the conclusion below a long introduction, gating the substance behind a form, and unsourced claims. Each one makes the page harder to quote with confidence.",
  },
  {
    q: "Where should a team with good SEO start on AEO?",
    a: "Take the pages that already rank, add a direct one-paragraph answer at the top, a sourced figure, and a comparison the model can lift, then measure mentions on the questions those pages target. That is the shortest path from existing rankings to citations.",
  },
];

export const Route = createFileRoute("/aeo-vs-seo")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Answer Engine Optimization", path: "/answer-engine-optimization" },
        { name: "AEO vs SEO", path: PATH },
      ],
      faqs: FAQS,
    }),
  component: Page,
});

function Page() {
  return (
    <MarketingPage
      stamp="Comparison"
      title="AEO vs SEO"
      dek="SEO earns a link in a list. AEO earns a sentence inside the answer written above that list. Most of the work overlaps; the part that does not is where brands lose."
      updated="October 2026"
      faqs={FAQS}
      faqTitle="Frequently asked questions about AEO and SEO"
      related={[
        {
          label: "Answer engine optimization, in full",
          to: "/answer-engine-optimization",
          description: "The definition, the framework, and how to evaluate tools",
        },
        {
          label: "Generative engine optimization guide",
          to: "/generative-engine-optimization",
          description: "Getting cited inside generative answers",
        },
        {
          label: "Free AI visibility check",
          to: "/check",
          description: "See how ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode answer for you",
        },
        {
          label: "How we measure",
          to: "/methodology",
          description: "Repeated checks, six AI surfaces and what each number means",
        },
      ]}
    >
      <Section heading="The one-line difference">
        <p>
          SEO asks: can a buyer find a link to us? AEO asks: when the assistant
          writes the answer and the buyer never opens a link, are we in it? The
          site is the same. The unit of success is not.
        </p>
        <Callout label="In one line">
          SEO competes for position in a list. AEO competes for inclusion in a
          sentence.
        </Callout>
      </Section>

      <Section heading="Side by side">
        <ComparisonTable
          headers={["Dimension", "SEO", "AEO"]}
          rows={[
            ["Unit of success", "A ranked link", "A named mention or cited source"],
            ["Buyer moment", "They search and scan results", "They ask and read one answer"],
            ["Page job", "Match the query, earn the click", "State the answer, be quotable"],
            ["Winning shape", "Depth, links, topical coverage", "Direct answer, table, sourced figure"],
            ["Metric", "Rank, clicks, impressions", "Mention rate, citation rate, answer position"],
            ["Feedback speed", "Weeks of rank movement", "Repeated runs, compared over time"],
            ["Failure mode", "Page two", "Page ranks, answer names a competitor"],
          ]}
        />
      </Section>

      <Section heading="What carries over, and what does not">
        <p>
          Answer engines retrieve from the indexed web. Everything that decides
          whether your page can be fetched, parsed and trusted still matters, so
          most of an SEO program is load-bearing for AEO.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-paper p-5">
            <h3 className="font-serif text-lg text-ink">Keep doing</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink/85">
              <li>Clean crawl paths and canonical clarity</li>
              <li>Fast pages that render their content without a click</li>
              <li>Real topical depth rather than thin pages per keyword</li>
              <li>Third-party mentions and reviews the model can corroborate</li>
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-paper p-5">
            <h3 className="font-serif text-lg text-ink">Add or change</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink/85">
              <li>Answer the question in the first paragraph, not the fifth</li>
              <li>Write to the buyer's question, not the keyword string</li>
              <li>Give figures a date and a source so they can be quoted</li>
              <li>Ungate what you want cited; forms are a wall to a model</li>
              <li>Measure mentions and citations, not only rank</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section heading="Why good rankings do not guarantee mentions">
        <p>
          Across{" "}
          {MEASURED.answers.toLocaleString()} assistant answers we recorded for{" "}
          {MEASURED.questions.toLocaleString()} tracked buyer questions,{" "}
          {MEASURED.anyBrandPct}% named at least one company, and the{" "}
          {MEASURED.citations.toLocaleString()} cited sources came from{" "}
          {MEASURED.domains.toLocaleString()} different domains. The answer is
          assembled from a scattered set of sources rather than the top of one
          results page, which is why a first-position page can sit unmentioned
          while a smaller, more quotable page is cited.
        </p>
        <p className="text-sm text-muted-foreground">
          {MEASURED_SOURCE}. Counted from our own scans across ChatGPT, Claude,
          Gemini and Perplexity.
        </p>
      </Section>

      <Section heading="Turning ranked pages into cited pages">
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            List the pages that already rank and the buyer question each one
            really answers.
          </li>
          <li>
            Put a direct two-sentence answer to that question at the top of the
            page, before the context.
          </li>
          <li>
            Add one checkable element the model can lift: a dated figure with a
            source, or a comparison table.
          </li>
          <li>
            Run those questions repeatedly on each assistant and record whether
            you are named, cited, or absent.
          </li>
          <li>
            Fix the questions where you are absent first. Absence is a bigger
            gap than a late mention.
          </li>
        </ol>
        <p>
          For the full discipline behind step three, read{" "}
          <Link
            to="/answer-engine-optimization"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            the answer engine optimization guide
          </Link>
          . For where GEO and LLMO fit alongside it, see{" "}
          <Link
            to="/llmo-vs-geo"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            LLMO vs GEO
          </Link>
          .
        </p>
      </Section>

      <Callout label="Next step">
        See which buyer questions already name you and which do not.{" "}
        <Link
          to="/check"
          className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
        >
          Run a free AI visibility check
        </Link>
        .
      </Callout>
    </MarketingPage>
  );
}
